import React, { TouchEventHandler, useCallback, useEffect, useMemo, useState } from 'react';

import { debounce, noop, uniqBy } from 'lodash';

import { ILocation } from '@rbi-ctg/frontend';
import { GoogleGeolocationLibraryLoader } from 'components/google-geolocation-library-loader';
import { useSendUpdateUserAttributesEventMutation } from 'generated/graphql-gateway';
import { IRestaurantNode } from 'generated/rbi-graphql';
import { useSelectRestaurant } from 'hooks/store-card';
import { IUseMap, useMap } from 'hooks/use-map';
import { useMediaQuery } from 'hooks/use-media-query';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useStoreContext } from 'state/store';

import { useMarkers } from '../../hooks/use-markers';
import { StoreLocatorMap } from '../../map';
import { StoreActions } from '../store-actions';
import { StoreCard } from '../store-card';

import { MapContainer } from './map-view.styled';

export interface IMapViewProps {
  activeCoordinates: ILocation | null;
  currentViewIsMap: boolean;
  handleResetLocation: VoidFunction;
  onSearchClick: (location: ILocation) => void;
  storesFavs: IRestaurantNode[];
  storesRecent: IRestaurantNode[];
  storesNearby: IRestaurantNode[];
  showStoreCard?: boolean;
  showStoreActions?: boolean;
  customElement?: JSX.Element;
}

const setupEvtListeners = (onDragEnd: TouchEventHandler) => ({
  dragend: debounce(onDragEnd, 200),
});

const MapViewStoreCard: React.FC<{
  restaurant: IRestaurantNode;
  showStoreCard: boolean;
  showStoreActions: boolean;
  customElement?: JSX.Element;
}> = ({ restaurant, showStoreCard, showStoreActions, customElement }) => {
  const { selectRestaurant } = useSelectRestaurant({ restaurant });

  useEffect(() => {
    if (restaurant && !showStoreActions) {
      selectRestaurant({ callback: noop });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant]);

  return (
    <>
      {customElement}
      {showStoreCard && <StoreCard restaurant={restaurant} />}
      {showStoreActions && <StoreActions restaurant={restaurant} />}
    </>
  );
};

export const MapView: React.FC<IMapViewProps> = ({
  activeCoordinates,
  currentViewIsMap,
  handleResetLocation,
  onSearchClick,
  storesFavs,
  storesNearby,
  storesRecent,
  showStoreCard = true,
  showStoreActions = true,
  customElement,
}) => {
  const isMobileFullScreen = useMediaQuery('mobileFullscreen');
  const isDesktop = useMediaQuery('desktop');
  const { trackEvent, updateUserAttributes } = useCdpContext();
  const [sendUpdateUserAttributesEvent] = useSendUpdateUserAttributesEventMutation();
  const { restaurantLogAttributesRef } = useStoreContext();

  const [selectedStore, setSelectedStore] = useState<IRestaurantNode | null>(null);
  const [hasPanned, setHasPanned] = useState(false);

  const eventListeners: IUseMap['eventListeners'] = useMemo(() => {
    return {
      ...setupEvtListeners(() => {
        setHasPanned(true);
      }),
      click: () => setSelectedStore(null),
    };
  }, []);

  const { center, createMarker, map, markers, panTo, zoom } = useMap(
    activeCoordinates
      ? {
          eventListeners,
          position: activeCoordinates,
        }
      : { eventListeners }
  );

  const onClickSearch = useCallback(() => {
    setHasPanned(false);

    onSearchClick(center);
  }, [center, onSearchClick]);

  /**
   * TODO:
   *  - should we still include favs & recents in map view, if we have values?
   *  - added recents here bc idk why they weren't there in current implementation
   */
  const storesWithMarkers = uniqBy([...storesNearby, ...storesFavs, ...storesRecent], '_id');

  useMarkers({
    createMarker,
    map,
    markers,
    storesFavs,
    panTo,
    storesNearby: storesWithMarkers,
    activeStoreId: selectedStore?._id,
    onClickMarker: (store: IRestaurantNode) => {
      if (!store._id || store._id === selectedStore?._id) {
        return;
      }

      trackEvent({
        name: CustomEventNames.BUTTON_CLICK,
        type: EventTypes.Other,
        attributes: {
          Name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
          StoreId: store._id,
          ...restaurantLogAttributesRef.current,
        },
      });
      updateUserAttributes(
        {
          'Sanity Restaurant ID': store._id,
        },
        {},
        sendUpdateUserAttributesEvent
      );

      setSelectedStore(store);

      // TODO: don't think we need this - just log the marker click
      // const { latitude: lat, longitude: lng } = store;

      // if (lat && lng) {
      //   return searchNearbyRestaurants({ location: { lat, lng } });
      // }
    },
  });

  return (
    <GoogleGeolocationLibraryLoader>
      <MapContainer>
        <StoreLocatorMap
          map={map}
          currentViewIsMap={currentViewIsMap}
          handleResetLocation={handleResetLocation}
          hasPanned={hasPanned}
          isDesktop={isDesktop}
          isMobileFullScreen={isMobileFullScreen}
          onClickSearch={onClickSearch}
          zoom={zoom}
        />
        {currentViewIsMap && selectedStore && (
          <MapViewStoreCard
            showStoreCard={showStoreCard}
            showStoreActions={showStoreActions}
            restaurant={selectedStore}
            customElement={customElement}
          />
        )}
      </MapContainer>

      {/* TODO: add dynamic store card for selected map view marker */}
    </GoogleGeolocationLibraryLoader>
  );
};
